import app from '@src/helpers/app';
import {IConfigEntries} from '@src/@interfaces/services/Config/IConfigEntries';
import {ConfigKeys} from '@src/@types/services/Config/ConfigKeys';
import {IConfig} from '@src/@interfaces/services/Config/IConfig';
import {default as client} from '@client/config/client.json';
import {default as settings} from '@client/config/user-settings.json';
import {default as languages} from '@client/config/languages';
import {getRunTimeConfig} from '@client/config/runtime-config';
import {IListener, ISubscription} from '@src/@interfaces/services/Events';
import {ISettings, SettingsKeys} from '@src/@interfaces/services/Settings/ISettings';
import Settings from '@src/services/Settings/Settings';
import DotAccessor from '@src/services/DotAccessor/DotAccessor';
import {IDotAccessor} from '@src/@interfaces/services/DotAccessor/IDotAccessor';

enum ConfigEvents {
    VALUE_CHANGED = 'config@value-changed',
}

export default class Config implements IConfig {
    protected entries: IConfigEntries = {client, languages, settings};
    protected settingsStore!: ISettings;

    constructor() {
        this.settingsStore = new Settings();

        this.refresh().then();
    }

    get<TRes = any>(key: ConfigKeys, defaultValue?: TRes): TRes {
        return this.accessor.get(this.entries, key, defaultValue);
    }

    set(key: ConfigKeys, value: any): void {
        this.accessor.set(this.entries, key, value);
        this.events.dispatch(ConfigEvents.VALUE_CHANGED, {config: this, key, value});
        this.settingsStore.set(key, value);
    }

    async refresh() {
        this.entries.client = getRunTimeConfig();

        const specificKeys: string[] = Object.values(SettingsKeys);
        const settings = this.settingsStore.getList();
        let accessor: IDotAccessor | null = null;
        for (const [key, value] of Object.entries(settings)) {
            if (!specificKeys.includes(key)) {
                if (!accessor) {
                    accessor = new DotAccessor();
                }

                accessor.set(this.entries, key, value);
            }
        }
    }

    onValueChanged(key: '*' | ConfigKeys | ConfigKeys[], listener: IListener<{
        config: IConfig,
        key: ConfigKeys,
        value: any
    }>): ISubscription {
        const keys = Array.isArray(key) ? key : [key];
        const wrappedListener: typeof listener = key === '*' ?
            listener :
            (event, payload) => {
                if (keys.some(key => key === payload.key)) {
                    listener(event, payload);
                }
            };

        return this.events.listen(ConfigEvents.VALUE_CHANGED, wrappedListener);
    }

    protected get accessor() {
        return app('dot-accessor');
    }

    protected get events() {
        return app('events');
    }
}
